import React, {useEffect, useState} from 'react'
import {
    Button,
    Chip,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material"
import {ApiKeyRequests} from "../../api/requests/ApiKeyRequests"
import {ApiKeyJson, CreatedApiKeyJson} from "../../api/json/ApiKeyJson"
import ApiError from "../../api/ApiError"
import AsyncButton from "../button/AsyncButton"
import ErrorMessage from "../error/ErrorMessage"
import ConfirmDeleteModal from "../modal/ConfirmDeleteModal"
import ApiKeyRevealModal from "./ApiKeyRevealModal"
import {RoleCheck} from "../../library/RoleCheck"

function ApiKeysTab() {
    const [keys, setKeys] = useState<ApiKeyJson[]>()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<ApiError>()

    const [name, setName] = useState("")
    const [creating, setCreating] = useState(false)

    const [createdKey, setCreatedKey] = useState<CreatedApiKeyJson | null>(null)
    const [revealOpen, setRevealOpen] = useState(false)

    const [keyToRevoke, setKeyToRevoke] = useState<ApiKeyJson | null>(null)
    const [revokeError, setRevokeError] = useState<ApiError>()

    const load = () => {
        setLoading(true)
        ApiKeyRequests.list()
            .then(result => {
                setKeys(result)
                setError(undefined)
            })
            .catch(setError)
            .finally(() => setLoading(false))
    }

    useEffect(load, [])

    const handleCreate = () => {
        setCreating(true)
        ApiKeyRequests.create(name.trim() || "API key")
            .then(created => {
                setCreatedKey(created)
                setRevealOpen(true)
                setName("")
                load()
            })
            .catch(setError)
            .finally(() => setCreating(false))
    }

    const handleRevoke = () => {
        if (!keyToRevoke) return
        ApiKeyRequests.revoke(keyToRevoke.id)
            .then(() => {
                setKeyToRevoke(null)
                load()
            })
            .catch(setRevokeError)
    }

    if (!RoleCheck.isAdmin()) {
        return <Typography color="text.secondary">Only organization admins can manage API keys.</Typography>
    }

    return (
        <Stack spacing={3} sx={{maxWidth: 720}}>
            <Typography variant="body2" color="text.secondary">
                API keys authenticate requests to the public API. Treat them like passwords.
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center">
                <TextField label="Name"
                           placeholder="e.g. Production"
                           value={name}
                           onChange={event => setName(event.target.value)}
                           size="small"
                           sx={{flex: 1}}/>
                <AsyncButton waiting={creating} onClick={handleCreate}>Create key</AsyncButton>
            </Stack>

            {error && <ErrorMessage error={error}/>}

            {loading && !keys && <CircularProgress/>}

            {keys && keys.length === 0 && <Typography color="text.secondary">No API keys yet.</Typography>}

            {keys && keys.length > 0 &&
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Key</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell>Last used</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {keys.map(key =>
                            <TableRow key={key.id}>
                                <TableCell>{key.name}</TableCell>
                                <TableCell sx={{fontFamily: "monospace"}}>{key.keyPrefix}…</TableCell>
                                <TableCell>{new Date(key.created).toLocaleDateString()}</TableCell>
                                <TableCell>{key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleDateString() : "Never"}</TableCell>
                                <TableCell align="right">
                                    {key.revoked
                                        ? <Chip label="Revoked" size="small"/>
                                        : <Button color="error"
                                                  size="small"
                                                  onClick={() => {
                                                      setRevokeError(undefined)
                                                      setKeyToRevoke(key)
                                                  }}>Revoke</Button>
                                    }
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            }

            <ApiKeyRevealModal createdKey={createdKey} open={revealOpen} setOpen={setRevealOpen}/>

            <ConfirmDeleteModal
                heading="Revoke API key"
                body={`Revoking "${keyToRevoke?.name ?? ""}" immediately breaks any integration using it. This cannot be undone.`}
                deleteButtonTitle="Revoke"
                open={keyToRevoke !== null}
                setOpen={open => { if (!open) setKeyToRevoke(null) }}
                confirmedDelete={handleRevoke}
                error={revokeError}
            />
        </Stack>
    )
}

export default ApiKeysTab
